import React, { useState } from 'react';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import OrganizationHero from './components/OrganizationHero';
import AboutSection from './components/AboutSection';
import OpportunitiesSection from './components/OpportunitiesSection';
import TestimonialsSection from './components/TestimonialsSection';
import ImpactMetrics from './components/ImpactMetrics';
import PhotoGallery from './components/PhotoGallery';
import ContactSection from './components/ContactSection';

const OrganizationImpactProfiles = () => {
  const [selectedOrganization] = useState({
    name: "Green Earth Initiative",
    category: "Environmental Conservation",
    established: 2015,
    icon: "Leaf",
    mission: "Empowering communities to protect and restore natural ecosystems through education, conservation projects, and sustainable practices that create lasting environmental impact.",
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e60a098e-1765893427294.png",
    heroImageAlt: "Group of diverse volunteers planting trees in urban park with city skyline in background during sunny day",
    tags: ["Environmental", "Education", "Community", "Sustainability"],
    stats: [
    { value: "50K+", label: "Trees Planted" },
    { value: "2,500", label: "Active Volunteers" },
    { value: "45", label: "Communities Served" }]

  });

  const aboutData = {
    subtitle: "Building a sustainable future through community-driven environmental action",
    description: `Founded in 2015, Green Earth Initiative emerged from a simple belief: that local communities hold the power to create meaningful environmental change. What started as a small group of passionate individuals planting trees in urban neighborhoods has grown into a comprehensive environmental movement spanning 45 communities.\n\nOur approach combines hands-on conservation work with education and advocacy, ensuring that every project creates both immediate impact and long-term sustainability. We believe that environmental stewardship isn't just about protecting nature—it's about building resilient communities where people and ecosystems thrive together.\n\nThrough partnerships with schools, businesses, and local governments, we've created a network of environmental champions who are transforming their communities one project at a time.`,
    values: [
    {
      icon: "Users",
      title: "Community-Centered",
      description: "We believe local communities are the most powerful agents of environmental change"
    },
    {
      icon: "Target",
      title: "Impact-Driven",
      description: "Every project is designed with measurable outcomes and long-term sustainability"
    },
    {
      icon: "BookOpen",
      title: "Education-Focused",
      description: "We empower people with knowledge and skills to become environmental stewards"
    },
    {
      icon: "Handshake",
      title: "Collaborative",
      description: "We build partnerships that amplify our impact and create lasting change"
    }],

    impactAreas: [
    { name: "Urban Reforestation", percentage: 85 },
    { name: "Water Conservation", percentage: 72 },
    { name: "Wildlife Habitat Restoration", percentage: 68 },
    { name: "Environmental Education", percentage: 90 }],

    certifications: [
    { name: "501(c)(3) Nonprofit Status", issuer: "IRS" },
    { name: "GuideStar Platinum Seal", issuer: "Candid" },
    { name: "Environmental Excellence Award", issuer: "EPA" },
    { name: "Community Impact Recognition", issuer: "United Way" }]

  };

  const opportunitiesData = [
  {
    id: 1,
    icon: "TreePine",
    title: "Urban Tree Planting Coordinator",
    description: "Lead weekend tree planting events in local neighborhoods. Train volunteers, coordinate logistics, and ensure proper planting techniques for maximum tree survival rates.",
    location: "Downtown District",
    schedule: "Saturdays, 8 AM - 2 PM",
    commitment: "4-6 hours/week",
    skills: ["Leadership", "Event Planning", "Environmental Science"],
    volunteers: 5,
    urgent: true
  },
  {
    id: 2,
    icon: "Droplets",
    title: "Water Quality Monitoring Specialist",
    description: "Collect and analyze water samples from local streams and rivers. Document findings and help educate community members about water conservation.",
    location: "Riverside Park",
    schedule: "Flexible weekdays",
    commitment: "3-4 hours/week",
    skills: ["Data Analysis", "Environmental Testing", "Report Writing"],
    volunteers: 3,
    urgent: false
  },
  {
    id: 3,
    icon: "GraduationCap",
    title: "Environmental Education Mentor",
    description: "Teach elementary school students about ecosystems, conservation, and sustainability through interactive workshops and outdoor activities.",
    location: "Local Schools",
    schedule: "Weekday afternoons",
    commitment: "2-3 hours/week",
    skills: ["Teaching", "Public Speaking", "Curriculum Development"],
    volunteers: 8,
    urgent: false
  },
  {
    id: 4,
    icon: "Camera",
    title: "Conservation Photography Volunteer",
    description: "Document our projects through compelling photography and videography. Help tell our impact story through visual storytelling.",
    location: "Various Sites",
    schedule: "Flexible",
    commitment: "5-8 hours/month",
    skills: ["Photography", "Video Editing", "Storytelling"],
    volunteers: 2,
    urgent: true
  },
  {
    id: 5,
    icon: "Wrench",
    title: "Community Garden Maintenance Lead",
    description: "Oversee maintenance of community gardens, coordinate volunteer work days, and teach sustainable gardening practices to community members.",
    location: "Westside Community Garden",
    schedule: "Sundays, 9 AM - 1 PM",
    commitment: "4 hours/week",
    skills: ["Gardening", "Project Management", "Community Engagement"],
    volunteers: 4,
    urgent: false
  },
  {
    id: 6,
    icon: "FileText",
    title: "Grant Writing Assistant",
    description: "Research funding opportunities and assist in writing grant proposals to support our environmental programs and expand our community impact.",
    location: "Remote/Office",
    schedule: "Flexible",
    commitment: "6-10 hours/month",
    skills: ["Grant Writing", "Research", "Nonprofit Management"],
    volunteers: 2,
    urgent: false
  }];


  const testimonialsData = [
  {
    id: 1,
    name: "Sarah Mitchell",
    role: "Tree Planting Coordinator",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10f2ac275-1763297173925.png",
    avatarAlt: "Professional headshot of Caucasian woman with shoulder-length brown hair wearing green outdoor jacket",
    rating: 5,
    content: "Volunteering with Green Earth Initiative has been transformative. I\'ve learned so much about urban forestry and met incredible people who share my passion for environmental conservation. Seeing the trees we planted three years ago now providing shade and habitat is incredibly rewarding.",
    duration: "3 years",
    hours: 450
  },
  {
    id: 2,
    name: "Marcus Johnson",
    role: "Water Quality Specialist",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_153a01ff6-1763295628568.png",
    avatarAlt: "Professional headshot of African American man with short black hair wearing blue polo shirt and glasses",
    rating: 5,
    content: "As a retired environmental scientist, I was looking for meaningful ways to stay engaged with conservation work. This organization gave me the perfect opportunity to use my expertise while mentoring the next generation of environmental stewards.",
    duration: "2 years",
    hours: 320
  },
  {
    id: 3,
    name: "Emily Chen",
    role: "Education Mentor",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12ffe0547-1763294341876.png",
    avatarAlt: "Professional headshot of Asian woman with long black hair wearing yellow cardigan and warm smile",
    rating: 5,
    content: "Teaching kids about environmental conservation has been the highlight of my week. The organization provides excellent training and resources, and seeing children's eyes light up when they learn about ecosystems makes every moment worthwhile.",
    duration: "1.5 years",
    hours: 180
  },
  {
    id: 4,
    name: "David Rodriguez",
    role: "Photography Volunteer",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0132445-1763294014923.png",
    avatarAlt: "Professional headshot of Hispanic man with short dark hair wearing black t-shirt holding camera",
    rating: 5,
    content: "Combining my passion for photography with environmental conservation has been amazing. I've documented incredible transformation stories and helped the organization showcase their impact to potential donors and volunteers.",
    duration: "1 year",
    hours: 95
  },
  {
    id: 5,
    name: "Jennifer Williams",
    role: "Garden Maintenance Lead",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165071dbb-1763298716209.png",
    avatarAlt: "Professional headshot of Caucasian woman with curly red hair wearing green gardening apron",
    rating: 5,
    content: "The community garden project has brought our neighborhood together in ways I never imagined. We\'re not just growing vegetables—we\'re growing community connections and teaching sustainable practices that will last for generations.",
    duration: "2.5 years",
    hours: 380
  },
  {
    id: 6,
    name: "Robert Taylor",
    role: "Grant Writing Assistant",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c496c0bc-1764782055012.png",
    avatarAlt: "Professional headshot of Caucasian man with gray hair wearing navy blue suit and tie",
    rating: 5,
    content: "Using my professional writing skills to help secure funding for environmental projects has been incredibly fulfilling. The organization\'s transparent approach and measurable impact make it easy to craft compelling grant proposals.",
    duration: "1 year",
    hours: 120
  }];


  const metricsData = {
    overview: [
    { icon: "TreePine", value: "50,000+", label: "Trees Planted", trend: "+15% this year" },
    { icon: "Users", value: "2,500", label: "Active Volunteers", trend: "+22% growth" },
    { icon: "MapPin", value: "45", label: "Communities", trend: "12 new this year" },
    { icon: "Clock", value: "125K", label: "Volunteer Hours", trend: "+18% increase" }],

    programs: [
    {
      name: "Urban Reforestation",
      completion: 85,
      participants: 1200,
      impact: "15,000 trees planted"
    },
    {
      name: "Water Conservation",
      completion: 72,
      participants: 850,
      impact: "8 watersheds restored"
    },
    {
      name: "Environmental Education",
      completion: 90,
      participants: 3500,
      impact: "45 schools reached"
    },
    {
      name: "Community Gardens",
      completion: 68,
      participants: 650,
      impact: "22 gardens established"
    }],

    engagement: [
    {
      icon: "TrendingUp",
      label: "Volunteer Retention",
      value: "87%",
      description: "Volunteers return for multiple projects"
    },
    {
      icon: "Award",
      label: "Skill Certifications",
      value: "450+",
      description: "Volunteers earned environmental certifications"
    },
    {
      icon: "Heart",
      label: "Satisfaction Rate",
      value: "4.9/5",
      description: "Average volunteer experience rating"
    },
    {
      icon: "Users",
      label: "Team Leaders",
      value: "120",
      description: "Volunteers advanced to leadership roles"
    }]

  };

  const photosData = [
  {
    id: 1,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f1f2f6f-1765057537093.png",
    imageAlt: "Group of diverse volunteers planting young trees in urban park with shovels and watering cans on sunny morning",
    title: "Community Tree Planting Day",
    location: "Downtown District",
    date: "November 2024"
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1643311249054-aae08a30a374",
    imageAlt: "Volunteers in blue shirts testing water quality in clear stream with testing equipment and notebooks",
    title: "Water Quality Monitoring",
    location: "Riverside Park",
    date: "October 2024"
  },
  {
    id: 3,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130d1e922-1764806428397.png",
    imageAlt: "Elementary school children sitting in circle outdoors learning about plants from volunteer instructor",
    title: "Environmental Education Workshop",
    location: "Lincoln Elementary School",
    date: "September 2024"
  },
  {
    id: 4,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130229536-1764666625703.png",
    imageAlt: "Lush community garden with raised beds full of vegetables and flowers with volunteers working in background",
    title: "Community Garden Harvest",
    location: "Westside Community Garden",
    date: "August 2024"
  },
  {
    id: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b733c819-1764684104737.png",
    imageAlt: "Volunteers cleaning up beach shoreline collecting plastic waste in bags during coastal cleanup event",
    title: "Coastal Cleanup Initiative",
    location: "Sunset Beach",
    date: "July 2024"
  },
  {
    id: 6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aac105d1-1765225937232.png",
    imageAlt: "Native wildflower meadow in full bloom with butterflies and bees with volunteers observing in distance",
    title: "Native Habitat Restoration",
    location: "Prairie Reserve",
    date: "June 2024"
  }];


  const contactData = {
    address: "456 Green Street, Suite 200\nEnvironmental District, EC 12345",
    phone: "(555) 123-4567",
    email: "info@greenearthinitiative.org",
    hours: "Monday - Friday: 9:00 AM - 5:00 PM\nSaturday: 10:00 AM - 2:00 PM",
    mapTitle: "Green Earth Initiative Office Location",
    mapLat: "40.7128",
    mapLng: "-74.0060",
    social: [
    { platform: "Facebook", icon: "Facebook", url: "https://facebook.com" },
    { platform: "Twitter", icon: "Twitter", url: "https://twitter.com" },
    { platform: "Instagram", icon: "Instagram", url: "https://instagram.com" },
    { platform: "LinkedIn", icon: "Linkedin", url: "https://linkedin.com" }]

  };

  const handleContactClick = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleVolunteerClick = () => {
    document.getElementById('opportunities-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleApplyClick = (opportunity) => {
    console.log('Apply clicked for:', opportunity);
    alert(`Thank you for your interest in "${opportunity?.title}"! We'll contact you soon with next steps.`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <OrganizationHero
          organization={selectedOrganization}
          onContactClick={handleContactClick}
          onVolunteerClick={handleVolunteerClick} />


        <AboutSection about={aboutData} />

        <div id="opportunities-section">
          <OpportunitiesSection
            opportunities={opportunitiesData}
            onApplyClick={handleApplyClick} />

        </div>

        <TestimonialsSection testimonials={testimonialsData} />

        <ImpactMetrics metrics={metricsData} />

        <PhotoGallery photos={photosData} />

        <div id="contact-section">
          <ContactSection contact={contactData} />
        </div>

        <section className="py-12 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Make a Difference?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Join our community of passionate volunteers and help create lasting environmental impact in your community.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={handleVolunteerClick}
                className="px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors shadow-lg flex items-center gap-2">

                <Icon name="Heart" size={20} />
                <span>Start Volunteering</span>
              </button>
              <button
                onClick={handleContactClick}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border-2 border-white/30 flex items-center gap-2">

                <Icon name="MessageCircle" size={20} />
                <span>Learn More</span>
              </button>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Icon name="Heart" size={16} color="white" />
              </div>
              <span className="font-semibold text-foreground">VolunteerHub</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} VolunteerHub. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-primary transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default OrganizationImpactProfiles;